import { Injectable, computed, signal } from '@angular/core';
import { SearchResult, SnackbarService, SnackbarType } from 'src/app/shared';
import { InventoryItem } from '../../models/inventory/inventory-item';
import { InventoryPendingForm } from '../../models/inventory/inventory-request';
import {
  isDeleted,
  applyStatusAfterSubmit,
} from '../../models/inventory/inventory-item-status';
import {
  InventorySubmissionState,
  buildDraftRequest,
  mapDraftToState,
} from '../../models/inventory/inventory-mapper';
import { UserActionService } from '../../../shared/services/user-action.service';
import { InventoryService } from './inventory.service';

@Injectable()
export class InventoryStateService {
  private readonly _items = signal<InventoryItem[]>([]);
  private readonly _nextItemId = signal(1);
  private readonly _hasSubmitted = signal(false);
  private readonly _hasPendingChangesAfterSubmit = signal(false);
  private readonly _autoSubmittedOn = signal<Date | null>(null);
  private readonly _autoSubmitReason = signal<string | null>(null);
  private readonly _editingItemId = signal<number | null>(null);
  private readonly _saving = signal(false);
  private readonly _claimClosed = signal(false);
  private readonly _formDirty = signal(false);
  private readonly _formResetRequests = signal(0);
  private readonly _draftDirty = signal(false);
  private readonly _parameterSectionValid = signal(false);
  private readonly _pendingForm = signal<InventoryPendingForm | null>(null);
  private readonly _loadedPendingForm = signal<InventoryPendingForm | null>(
    null,
  );

  readonly items = this._items.asReadonly();
  readonly nextItemId = this._nextItemId.asReadonly();
  readonly hasSubmitted = this._hasSubmitted.asReadonly();
  readonly hasPendingChangesAfterSubmit =
    this._hasPendingChangesAfterSubmit.asReadonly();
  readonly autoSubmittedOn = this._autoSubmittedOn.asReadonly();
  readonly autoSubmitReason = this._autoSubmitReason.asReadonly();
  readonly editingItemId = this._editingItemId.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly claimClosed = this._claimClosed.asReadonly();

  readonly formDirty = this._formDirty.asReadonly();
  readonly draftDirty = this._draftDirty.asReadonly();

  readonly formResetRequests = this._formResetRequests.asReadonly();

  readonly loadedPendingForm = this._loadedPendingForm.asReadonly();

  readonly isEditing = computed(() => this._editingItemId() !== null);

  readonly editingItem = computed(
    () =>
      this._items().find((item) => item.id === this._editingItemId()) ?? null,
  );

  readonly visibleItems = computed(() =>
    this._items().filter((item) => !isDeleted(item.status)),
  );

  readonly submitDisabled = computed(
    () =>
      this._items().length === 0 ||
      (this._hasSubmitted() && !this._hasPendingChangesAfterSubmit()),
  );

  readonly saveForLaterDisabled = computed(() => {
    if (this._saving() || this._claimClosed()) {
      return true;
    }

    return this._items().length === 0 && !this._parameterSectionValid();
  });

  readonly cancelDisabled = computed(() => false);

  readonly showAutoSubmitToast = computed(
    () =>
      this._autoSubmitReason() === 'AutoTransmit' &&
      this._autoSubmittedOn() != null,
  );

  private source = '';
  private claimId = '';
  private claim: SearchResult = <SearchResult>{};

  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSync: Promise<boolean> | null = null;
  private static readonly SyncDebounceMs = 750;

  constructor(
    private inventoryService: InventoryService,
    private snackbarService: SnackbarService,
    private userAction: UserActionService,
  ) {}

  configure(source: string, claimId: string, claim: SearchResult): void {
    this.source = source;
    this.claimId = claimId;
    this.claim = claim;
    this._claimClosed.set(
      claim?.status?.toLowerCase().includes('closed') ?? false,
    );
  }

  async load(): Promise<void> {
    const draft = await this.inventoryService.getDraft(
      this.source,
      this.claimId,
      this.claim.lob,
    );

    if (draft?.items?.length || draft?.pendingForm) {
      this.apply(mapDraftToState(draft));
    }
  }

  addItem(item: InventoryItem): void {
    this._items.update((items) => [...items, item]);
    this._nextItemId.set(item.id + 1);
    this.markDraftDirty();
    this.markModified();
    this.scheduleSync();
  }

  updateItem(item: InventoryItem): void {
    this._items.update((items) =>
      items.map((existing) =>
        existing.id === item.id ? { ...existing, ...item } : existing,
      ),
    );
    this._editingItemId.set(null);
    this.markDraftDirty();
    this.markModified();
    this.scheduleSync();
  }

  replaceItems(items: InventoryItem[]): void {
    this._items.set(items);

    const editingId = this._editingItemId();
    if (editingId !== null && !items.some((item) => item.id === editingId)) {
      this._editingItemId.set(null);
    }

    this.markDraftDirty();
    this.markModified();
    this.scheduleSync();
  }

  startEditing(itemId: number): void {
    this._editingItemId.set(itemId);
  }

  stopEditing(): void {
    this._editingItemId.set(null);
  }

  setFormDirty(dirty: boolean): void {
    this._formDirty.set(dirty);
  }

  setParameterDraft(draft: InventoryPendingForm | null, valid: boolean): void {
    const ready = valid && draft != null;
    this._parameterSectionValid.set(ready);
    this._pendingForm.set(ready ? draft : null);
  }

  markModified(): void {
    this._hasPendingChangesAfterSubmit.set(true);
  }

  private markDraftDirty(): void {
    this._draftDirty.set(true);
  }

  async saveForLater(): Promise<boolean> {
    this._saving.set(true);

    try {
      const saved = await this.sync(true);

      if (saved) {
        this._draftDirty.set(false);
        this.dismissAutoSubmitToast();
        this._formDirty.set(false);

        // Sets SAVE_FOR_LATER_DATE, which is what the dashboard reminder keys off.
        this.userAction.setBustCache(true);
      }

      return saved;
    } finally {
      this._saving.set(false);
    }
  }

  markSubmitted(): void {
    this._items.update(applyStatusAfterSubmit);
    this._editingItemId.set(null);
    this._hasSubmitted.set(true);
    this._hasPendingChangesAfterSubmit.set(false);
    this._pendingForm.set(null);
    this._parameterSectionValid.set(false);
    this._loadedPendingForm.set(null);
    this._draftDirty.set(false);
    this.dismissAutoSubmitToast();
    this._formResetRequests.update((count) => count + 1);

    // A genuine submit clears SAVE_FOR_LATER_DATE, so the reminder should go away.
    this.userAction.setBustCache(true);
  }

  async clear(): Promise<void> {
    this.cancelPendingSync();

    await this.inventoryService.clearDraft(
      this.source,
      this.claimId,
      this.claim.lob,
      this.claim,
    );

    this.userAction.setBustCache(true);
  }

  async flush(): Promise<boolean> {
    if (
      this.syncTimer === null &&
      this.pendingSync === null &&
      !this._draftDirty()
    ) {
      return true;
    }

    this.cancelPendingSync();
    return this.sync(false);
  }

  dispose(): void {
    this.cancelPendingSync();
  }

  private apply(state: InventorySubmissionState): void {
    this._items.set(state.items);
    this._nextItemId.set(state.nextItemId);
    this._hasSubmitted.set(state.hasSubmitted);
    this._hasPendingChangesAfterSubmit.set(state.hasPendingChangesAfterSubmit);
    this._autoSubmittedOn.set(state.autoSubmittedOn ?? null);
    this._autoSubmitReason.set(state.autoSubmitReason ?? null);
    this._draftDirty.set(false);

    const pendingForm = state.hasSubmitted ? null : (state.pendingForm ?? null);
    this._pendingForm.set(pendingForm);
    this._parameterSectionValid.set(pendingForm != null);
    this._loadedPendingForm.set(pendingForm);
  }

  private dismissAutoSubmitToast(): void {
    this._autoSubmittedOn.set(null);
    this._autoSubmitReason.set(null);
  }

  private cancelPendingSync(): void {
    if (this.syncTimer !== null) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private scheduleSync(): void {
    this.cancelPendingSync();

    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      void this.syncAndReportFailure();
    }, InventoryStateService.SyncDebounceMs);
  }

  private async syncAndReportFailure(): Promise<void> {
    if (!this.isConfigured) {
      return;
    }

    let saved = false;

    try {
      saved = await this.sync(false);
    } catch {
      saved = false;
    }

    if (!saved) {
      this.snackbarService.show(
        'Your inventory changes could not be saved. Please try again.',
        SnackbarType.Error,
      );
    }
  }

  private get isConfigured(): boolean {
    return !!this.source && !!this.claimId && !!this.claim?.lob;
  }

  private async sync(markSaveForLater: boolean): Promise<boolean> {
    if (!this.isConfigured) {
      return false;
    }

    const request = this.inventoryService.saveDraft(
      this.source,
      this.claimId,
      this.claim.lob,
      buildDraftRequest({
        items: this._items(),
        nextItemId: this._nextItemId(),
        hasSubmitted: this._hasSubmitted(),
        hasPendingChangesAfterSubmit: this._hasPendingChangesAfterSubmit(),
        pendingForm: this._pendingForm(),
      }),
      this.claim,
      markSaveForLater,
    );

    this.pendingSync = request.then((response) => response?.success === true);

    try {
      return await this.pendingSync;
    } finally {
      this.pendingSync = null;
    }
  }
}
